import React from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  StudentPaymentSchemeWithMetrics,
  PaymentAddons,
  PaymentAddonsStatus
} from '../../../types/paymentScheme';
import { StyledButton } from '../styled';
import { usersWithConcessionApprovalAccess } from '../../../constants/superAdminUsers';
import { PaymentUser } from '../../../types/userType';
import { AdminUserRoles } from '../../../enums';
import Icons from '../../../components/icons';
import { IconButton } from '@mui/material';

export const getFeeDetailsColumns = (
  feeDetails: StudentPaymentSchemeWithMetrics[],
  onApproveRejectClick: (scheme: StudentPaymentSchemeWithMetrics) => void,
  onUpdateFeeStatusClick: (scheme: StudentPaymentSchemeWithMetrics) => void,
  hideCustomColumns: boolean,
  paymentUser: PaymentUser
): GridColDef[] => {
  const firstRow = feeDetails[0];
  const totalAmountPayable = firstRow?.totalAmountPayable || 0;
  const totalAmountPaid = firstRow?.totalAmountPaid || 0;
  const totalPendingAmount = firstRow?.totalPendingAmount || 0;

  const baseColumns: GridColDef[] = [
    { field: 'component', headerName: 'Component', width: 150 },
    { field: 'particulars', headerName: 'Particulars', width: 200 },
    { field: 'feePeriod', headerName: 'Fee Period', width: 150 },
    { field: 'academicYear', headerName: 'Academic Year', width: 150 },
    {
      field: 'amount',
      headerName: hideCustomColumns
        ? 'Amount Payable'
        : `Amount Payable (${totalAmountPayable})`,
      type: 'number',
      width: 220
    },
    {
      field: 'paidAmount',
      headerName: hideCustomColumns
        ? 'Amount Paid'
        : `Amount Paid (${totalAmountPaid})`,
      type: 'number',
      width: 220
    },
    {
      field: 'pendingAmount',
      headerName: hideCustomColumns
        ? 'Pending Amount'
        : `Pending Amount (${totalPendingAmount})`,
      type: 'number',
      width: 220
    },
    { field: 'paymentDueDate', headerName: 'Payment Due Date', width: 180 },
    {
      field: 'paymentStatus',
      headerName: 'Fee Status',
      width: 150
    },
    { field: 'description', headerName: 'Description', width: 200 },
    { field: 'scheme', headerName: 'Scheme', width: 150 },
    {
      field: 'concessions',
      headerName: 'Concessions',
      width: 360,
      minWidth: 280,
      flex: 1,
      valueGetter: (params: GridRenderCellParams<PaymentAddons[]>) => {
        const concessions: PaymentAddons[] =
          (params as unknown as PaymentAddons[]) || [];
        return concessions
          .filter(
            (concession: PaymentAddons) =>
              concession.status &&
              ![PaymentAddonsStatus.REJECTED].includes(concession.status)
          )
          .map(
            (concession: PaymentAddons) =>
              `${concession.description} - ${concession.amount}`
          )
          .join(', ');
      }
    },
    {
      field: 'waivers',
      headerName: 'Waivers',
      width: 360,
      minWidth: 280,
      flex: 1,
      valueGetter: (params: GridRenderCellParams<PaymentAddons[]>) => {
        const waivers: PaymentAddons[] =
          (params as unknown as PaymentAddons[]) || [];
        return waivers
          .filter(
            (waiver: PaymentAddons) =>
              waiver.status &&
              ![PaymentAddonsStatus.REJECTED].includes(waiver.status)
          )
          .map(
            (waiver: PaymentAddons) =>
              `${waiver.description} - ${waiver.amount}`
          )
          .join(', ');
      }
    },
    {
      field: 'ENACH Registered ?',
      headerName: 'ENACH Registered ?',
      width: 180,
      type: 'boolean',
      renderCell: (
        params: GridRenderCellParams<StudentPaymentSchemeWithMetrics, boolean>
      ) => {
        const isEnachRegistered = params.value;
        return isEnachRegistered ? 'Yes' : 'No';
      }
    }
  ];

  if (hideCustomColumns) {
    return baseColumns;
  }

  const customColumnsBeforeBase: GridColDef[] = [];

  if (paymentUser?.role === AdminUserRoles.FEE_ADMIN) {
    customColumnsBeforeBase.push({
      field: 'updateFeeStatus',
      headerName: 'Edit',
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (
        params: GridRenderCellParams<StudentPaymentSchemeWithMetrics>
      ) => {
        return (
          <IconButton
            size='small'
            onClick={() => onUpdateFeeStatusClick(params.row)}
            sx={{ padding: '4px' }}
          >
            <Icons.EditBlackOutlined />
          </IconButton>
        );
      }
    });
  }

  const customColumnsAfterBase: GridColDef[] = [];

  if (usersWithConcessionApprovalAccess.includes(paymentUser?.email || '')) {
    customColumnsAfterBase.push({
      field: 'concessionsAndWaiversAction',
      headerName: 'Concessions/Waivers',
      width: 180,
      renderCell: (
        params: GridRenderCellParams<StudentPaymentSchemeWithMetrics>
      ) => {
        const hasConcessionsOrWaiversInApproval =
          params.row.concessions?.some(
            (c) => c.status === PaymentAddonsStatus.IN_APPROVAL
          ) ||
          params.row.waivers?.some(
            (w) => w.status === PaymentAddonsStatus.IN_APPROVAL
          );

        return (
          <StyledButton
            variant='contained'
            size='small'
            onClick={() => onApproveRejectClick(params.row)}
            disabled={!hasConcessionsOrWaiversInApproval}
          >
            Approve/Reject
          </StyledButton>
        );
      }
    });
  }

  return [
    ...customColumnsBeforeBase,
    ...baseColumns,
    ...customColumnsAfterBase
  ];
};
